// Shared recovery for failed dynamic imports (stale chunks after a new build).
const RELOAD_KEY = "gf:module-import-reload";

export const isChunkLoadError = (error: unknown): boolean => {
  const msg = String((error as Error)?.message ?? error ?? "");
  return /importing a module script|failed to fetch dynamically|loading chunk|error loading dynamically|dynamically imported module|module script failed/i.test(
    msg,
  );
};

/** Reload with a cache-buster at most once every 10s. Returns false if throttled. */
export const reloadForFreshChunks = (): boolean => {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  } catch {
    /* storage unavailable */
  }
  if (Date.now() - last < 10000) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }

  const go = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("bust", String(Date.now()));
    window.location.replace(url.toString());
  };

  (async () => {
    try {
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      /* best effort */
    }
    go();
  })();
  return true;
};

/** Retry a dynamic import once (network blip), then fall back to a fresh reload. */
export const importWithRecovery = async <T,>(factory: () => Promise<T>): Promise<T> => {
  try {
    return await factory();
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;
    await new Promise((r) => setTimeout(r, 400));
    try {
      return await factory();
    } catch (retryError) {
      if (reloadForFreshChunks()) {
        // Keep Suspense fallback showing while the page reloads.
        return new Promise<T>(() => {});
      }
      throw retryError;
    }
  }
};
